'use client';

import { useState } from 'react';
import ResourceCard from './ResourceCard';
import Icon from '@/components/ui/AppIcon';

interface Resource {
  id: string;
  name: string;
  description: string;
  link: string | { url: string; label: string }[];
  logo: string;
  category: string;
  highlights: string[];
  bestFor: string[];
}

const UsefulResourcesInteractive = () => {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  const resources: Resource[] = [
    {
      id: 'visit-cyprus',
      name: 'Cyprus Scuba Diving – Official Guide',
      description: '🇨🇾🤿 The official scuba diving information page of the Cyprus Deputy Ministry of Tourism provides verified, up-to-date guidance on diving in Cyprus, including recognised dive sites, local regulations and safety requirements for recreational scuba divers and training activities across the island. This is the authoritative starting point for anyone planning scuba diving holidays in Cyprus, discovering certified dive locations, and understanding the national framework for recreational and sports diving.',
      link: 'https://www.visitcyprus.com/discover-cyprus/sports-training/sports-diving/',
      logo: '/assets/images/visitcyprus-logo_1_-1769759923272.png',
      category: 'Official Tourism',
      highlights: [
        '🐢 Official, government-backed source for scuba diving in Cyprus',
        '📍 Overview of recognised and promoted Cyprus dive sites',
        '⚓ Information aligned with local diving regulations and safety standards',
        '🤿 Reliable background for divers planning trips, courses or dive training',
        '🗺️ Reference point for officially supported dive areas and initiatives'
      ],
      bestFor: [
        '🐠 First-time visitors to Cyprus',
        '🤿 Divers planning their first dives on the island',
        '📅 Trip planning and destination research',
        '📘 Understanding the official scuba diving framework in Cyprus'
      ]
    },
    {
      id: 'limassol-tourism',
      name: '🏛️ Limassol Tourism Board – Official Limassol & Scuba Diving Guide',
      description: 'The official destination and visitor information platform of Limassol provides comprehensive, up-to-date travel guidance for visitors exploring Limassol and the surrounding region of Cyprus. It covers accommodation, transport, beaches, cultural attractions, events, outdoor activities and practical travel information, and is one of the most reliable public sources for planning holidays in Limassol. The Limassol Tourism Board also maintains a dedicated official scuba diving page, highlighting diving opportunities, local dive areas and underwater experiences along the Limassol coastline — making it a useful reference for divers planning scuba diving in Limassol, Cyprus.',
      link: [
        { url: 'https://www.limassoltourism.com', label: 'Visit Main Website' },
        { url: 'https://www.limassoltourism.com/diving/', label: 'Official Scuba Diving Guide' }
      ],
      logo: '/assets/images/356701875_575849974700765_6984119328263317632_n-1769855086139.jpg',
      category: 'Official Tourism',
      highlights: [
        '🏛️ Official destination and tourism authority for Limassol, Cyprus',
        '📍 Trusted public source for Limassol travel and visitor information',
        '🤿 Includes an official scuba diving section focused on Limassol dive sites',
        '🧭 Supports safe, well-planned diving and travel experiences in Cyprus',
        '🌊 Complements independent dive site and marine life information on Cyprus Diving'
      ],
      bestFor: [
        '🐠 Divers planning scuba diving in Limassol, Cyprus',
        '🧳 Visitors researching Limassol holidays and activities',
        '🗺️ First-time travellers to Limassol',
        '📅 Trip planning and destination research for Cyprus travel and diving holidays'
      ]
    },
    {
      id: 'larnaca-tourism',
      name: '🏛️ Larnaca Tourism Board – Official Larnaca & Scuba Diving Guide',
      description: 'The official destination and visitor information platform for the Larnaca region provides comprehensive and up-to-date travel guidance for visitors exploring Larnaca and the surrounding coastline of Cyprus. It covers accommodation, transport, beaches, cultural attractions, events, outdoor activities and practical travel information, and is a trusted public source for planning holidays in Larnaca. The Larnaca Tourism Board also maintains a dedicated official scuba diving page, highlighting recognised dive sites, underwater attractions and diving experiences around the Larnaca coastline — including one of Cyprus\' most famous wreck-diving destinations.',
      link: [
        { url: 'https://larnakaregion.com/', label: 'Visit Main Website' },
        { url: 'https://larnakaregion.com/en/things-do/diving', label: 'Official Scuba Diving Guide' }
      ],
      logo: '/assets/images/445000873_853366006826682_1820013187962434039_n-1769855410333.jpg',
      category: 'Official Tourism',
      highlights: [
        '🏛️ Official destination and tourism authority for Larnaca, Cyprus',
        '📍 Trusted public source for Larnaca travel and visitor information',
        '🤿 Includes an official scuba diving section focused on Larnaca dive sites',
        '⚓ Covers the Zenobia wreck area and surrounding recreational diving locations',
        '🧭 Supports safe, well-planned diving and travel experiences in Cyprus',
        '🌊 Complements independent dive site and marine life information on Cyprus Diving'
      ],
      bestFor: [
        '🐠 Divers planning scuba diving in Larnaca, Cyprus',
        '🧳 Visitors researching Larnaca holidays and activities',
        '🗺️ First-time travellers to the Larnaca region',
        '📅 Trip planning and destination research for Cyprus travel and diving holidays'
      ]
    },
    {
      id: 'dept-fisheries',
      name: '🐟 Department of Fisheries and Marine Research Cyprus',
      description: 'The Department of Fisheries and Marine Research is the official government authority responsible for the protection, management and sustainable use of marine and coastal resources in Cyprus. It plays a central role in marine biodiversity conservation, fisheries management, artificial reefs, marine protected areas and the monitoring of coastal and offshore ecosystems across Cyprus. For divers and visitors, the Department provides the authoritative framework behind many protected dive sites, conservation zones and environmentally sensitive marine areas, helping ensure that scuba diving in Cyprus is conducted safely, responsibly and in line with national environmental regulations. This resource is essential for anyone seeking reliable, government-backed information on marine conservation, fisheries policies and the protection of Cyprus\' underwater heritage.',
      link: 'https://www.moa.gov.cy/moa/dfmr/dfmr.nsf/index_en/index_en?OpenDocument',
      logo: '/assets/images/472145420_911639231158784_2754546932028234573_n-1769854428797.jpg',
      category: 'Official Tourism',
      highlights: [
        '🐢 Official government authority for marine protection and fisheries management in Cyprus',
        '📍 Information on marine protected areas and environmentally sensitive zones',
        '⚓ Oversight of artificial reefs and habitat enhancement projects',
        '🌊 Guidance supporting sustainable and responsible diving practices in Cyprus',
        '📊 Scientific monitoring of marine ecosystems, biodiversity and coastal waters'
      ],
      bestFor: [
        '🐠 Divers interested in marine conservation and protected dive sites in Cyprus',
        '📘 Understanding environmental rules and protected areas affecting scuba diving in Cyprus',
        '🧭 Trip planning around conservation zones and sensitive marine habitats',
        '🌍 Eco-conscious divers and underwater photographers'
      ]
    },
    {
      id: 'mer-lab',
      name: 'Marine & Environmental Research (MER Lab) - Cyprus',
      description: 'Independent scientific research and environmental consultancy for Cyprus\' marine ecosystems. MER Lab is an established Cyprus-based research organisation specialising in marine biology, ecology, aquaculture and fisheries. For more than 15 years, MER has led and delivered coastal and offshore research projects across Cyprus and currently plays a key role as a governmental scientific consultant, working closely with authorities, the fishing sector, divers and other sea users. The team combines advanced scientific expertise, modern analytical tools and field-proven methodologies to support marine environmental monitoring, impact assessments and applied research in Cyprus waters.',
      link: 'https://merresearch.com/',
      logo: '/assets/images/462617064_3665011177095006_7626686400421776453_n-1769853425926.png',
      category: 'Research & Science',
      highlights: [
        '🧪 Leading marine science and environmental monitoring organisation in Cyprus',
        '🌍 Long-term research across coastal and offshore areas of the island',
        '🤝 Scientific advisor to government bodies and marine stakeholders',
        '📊 High-quality data supporting marine protection, planning and policy',
        '🪸 Strong links with divers, fisheries and other sea users',
        '🔬 Marine biology and ecology research',
        '🐟 Fisheries and aquaculture studies',
        '🌊 Coastal and offshore environmental monitoring',
        '📈 Environmental impact assessments and consulting',
        '🧭 Underwater surveys and benthic monitoring',
        '⚙️ Full field equipment for underwater surveys and sampling',
        '🧫 Laboratory facilities for biotic and abiotic data analysis',
        '📡 Monitoring of physical, chemical and biological parameters in the water column and seabed'
      ],
      bestFor: [
        '🐠 Divers and organisations interested in marine conservation science in Cyprus',
        '🏛 Policy, planning and environmental compliance projects',
        '🧭 Researchers, NGOs and conservation initiatives',
        '📊 Evidence-based marine protection and sustainability work'
      ]
    },
    {
      id: 'enalia-physis',
      name: '🌿 Enalia Physis Environmental Research Centre',
      description: 'Non-profit environmental research and conservation organisation in Cyprus, working for the protection of marine and terrestrial ecosystems. Enalia Physis Environmental Research Centre is a Cyprus-based non-profit environmental organisation working towards the conservation of marine and terrestrial life and the promotion of a sustainable future for Cyprus. The organisation combines scientific research, public engagement and education to support evidence-based environmental protection and policy.',
      link: 'https://enaliaphysis.org.cy/',
      logo: '/assets/images/logo_new1_1_-1769853652220.png',
      category: 'Research & Science',
      highlights: [
        '🌍 Non-profit environmental organisation officially established in Cyprus',
        '🔬 Active scientific research in marine and coastal ecosystems',
        '🏛 Supports environmental decision-making and conservation policy',
        '🎓 Strong focus on education, outreach and public awareness',
        '📚 Free access to environmental reports and educational material',
        '🐟 Fisheries science',
        '🪸 Marine benthic and cave ecology',
        '🏞️ Marine protected areas',
        '🦠 Invasive species',
        '🗑️ Marine litter',
        '🌊 Coastal ecology',
        '🌍 Environmental and social sciences',
        '📘 Education and public awareness central to activities',
        '🎯 Promotes environmental conservation through political, cultural and social change',
        '📊 Develops outreach initiatives, public events and learning programmes',
        '📑 Scientific and technical reports openly available',
        '🎓 Awareness and educational material',
        '🔍 Identification slates and supporting tools',
        '📚 Contributes to scientific publications and conferences'
      ],
      bestFor: [
        '🐠 Divers interested in marine conservation and environmental science in Cyprus',
        '🌊 Community groups and NGOs active in marine protection',
        '🎓 Students, educators and researchers',
        '📊 Organisations seeking reliable environmental and ecological data',
        '🪸 Public awareness and citizen-science initiatives'
      ]
    }
  ];

  const categories = ['all', 'Official Tourism', 'Research & Science', 'NGOs', 'Dive Organizations', 'Conservation'];

  const filteredResources = selectedCategory === 'all' 
    ? resources 
    : resources.filter(r => r.category === selectedCategory);

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary/10 via-secondary/10 to-accent/10 py-12 lg:py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 lg:w-20 lg:h-20 bg-primary/10 rounded-full mb-6">
              <Icon name="BookOpenIcon" size={40} className="text-primary" />
            </div>
            <h1 className="text-4xl lg:text-5xl font-headline font-bold text-foreground mb-4">
              External Sources for Scuba Diving and Marine Information in Cyprus
            </h1>
            <p className="text-base lg:text-lg text-muted-foreground max-w-3xl mx-auto mb-4">
              External websites and organisations providing additional information related to scuba diving, marine research and environmental protection in Cyprus. CyprusDiving.info is an independent, non-commercial information platform and is not affiliated with, and does not endorse, the organisations listed below.
            </p>
            <p className="text-lg lg:text-xl text-muted-foreground max-w-2xl mx-auto">
              Curated links to official Cyprus diving information, tourism resources, NGOs, and essential organizations for divers
            </p>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="bg-card border-b border-border sticky top-16 lg:top-20 z-40">
        <div className="container mx-auto px-4 py-4">
          <div className="flex flex-wrap gap-2 justify-center">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-full font-medium transition-all duration-300 ${
                  selectedCategory === category
                    ? 'bg-primary text-primary-foreground shadow-md'
                    : 'bg-muted text-muted-foreground hover:bg-primary/10'
                }`}
              >
                {category === 'all' ? 'All Resources' : category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Resources Grid */}
      <section className="py-12 lg:py-16">
        <div className="container mx-auto px-4">
          {filteredResources.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 max-w-6xl mx-auto">
              {filteredResources.map((resource) => (
                <ResourceCard key={resource.id} resource={resource} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <Icon name="FolderOpenIcon" size={64} className="text-muted-foreground mx-auto mb-4" />
              <p className="text-xl text-muted-foreground">
                No resources found in this category yet.
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                Check back soon as we add more resources!
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Info Banner */}
      <section className="bg-primary/5 border-t border-border py-8">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <Icon name="InformationCircleIcon" size={32} className="text-primary mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-foreground mb-2">
              Know a useful resource?
            </h3>
            <p className="text-muted-foreground">
              This page is continuously expanding. We welcome suggestions for official diving organizations, NGOs, and trusted resources for the Cyprus diving community.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default UsefulResourcesInteractive;
